import { useFormContext } from 'react-hook-form';
import Accordion from 'storybookConfig/stories/cells/Accordion';
import Body from 'storybookConfig/stories/molecules/Body';
import PlatformDecisionButton from 'storybookConfig/stories/molecules/Button/DecisionButton/PlatformDecisionButton';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import TertiaryButton from 'storybookConfig/stories/molecules/Button/TertiaryButton';
import Heading from 'storybookConfig/stories/molecules/Heading';
import SupportLink from 'storybookConfig/stories/molecules/Link/SupportLink';
import ProgressBar from 'storybookConfig/stories/organisms/ProgressBar';
import { CommerceType, Platform } from 'types/models/company';
import { ButtonGrid, FooterRow, HeaderContent, SectionContent, StepContent } from './Shared.styles';

const BuyerPlatformFields = () => {
  const { setValue, watch } = useFormContext();

  const platform = watch('platform');

  return (
    <ButtonGrid>
      <PlatformDecisionButton
        variant={Platform.Shopify}
        isActive={platform === Platform.Shopify}
        onClick={() => setValue('platform', Platform.Shopify)}
      />

      <PlatformDecisionButton
        variant={Platform.Other}
        isActive={platform === Platform.Other}
        onClick={() => setValue('platform', Platform.Other)}
      >
        Carro API
      </PlatformDecisionButton>
    </ButtonGrid>
  );
};

const SellerPlatformFields = () => {
  const { setValue, watch } = useFormContext();

  const platform = watch('platform');

  return (
    <SectionContent>
      <ButtonGrid>
        <PlatformDecisionButton
          variant={Platform.Shopify}
          isActive={platform === Platform.Shopify}
          onClick={() => setValue('platform', Platform.Shopify)}
        />

        <PlatformDecisionButton
          variant={Platform.WooCommerce}
          isActive={platform === Platform.WooCommerce}
          onClick={() => setValue('platform', Platform.WooCommerce)}
        />

        <PlatformDecisionButton
          variant={Platform.BigCommerce}
          isActive={platform === Platform.BigCommerce}
          onClick={() => setValue('platform', Platform.BigCommerce)}
        />

        <PlatformDecisionButton
          variant={Platform.Magento2}
          isActive={platform === Platform.Magento2}
          onClick={() => setValue('platform', Platform.Magento2)}
        />
      </ButtonGrid>

      <Accordion.Root>
        <Accordion.Head text="Additional Syncing Options" />

        <Accordion.Body>
          <SectionContent>
            <PlatformDecisionButton
              variant={Platform.CsvSftp}
              isActive={platform === Platform.CsvSftp}
              onClick={() => setValue('platform', Platform.CsvSftp)}
            />

            <PlatformDecisionButton
              variant={Platform.ExternalSftp}
              isActive={platform === Platform.ExternalSftp}
              onClick={() => setValue('platform', Platform.ExternalSftp)}
            />

            <PlatformDecisionButton
              variant={Platform.EdiSftp}
              isActive={platform === Platform.EdiSftp}
              onClick={() => setValue('platform', Platform.EdiSftp)}
            />

            <PlatformDecisionButton
              variant={Platform.EdiExternalSftp}
              isActive={platform === Platform.EdiExternalSftp}
              onClick={() => setValue('platform', Platform.EdiExternalSftp)}
            />

            <PlatformDecisionButton
              variant={Platform.Other}
              isActive={platform === Platform.Other}
              onClick={() => setValue('platform', Platform.Other)}
            >
              Manage your data within the Carro App or use our API
            </PlatformDecisionButton>
          </SectionContent>
        </Accordion.Body>
      </Accordion.Root>
    </SectionContent>
  );
};

interface PlatformSelectionProps {
  currentStep: number;
  totalSteps: number;
  onNextButtonClick: () => void;
  onBackButtonClick: () => void;
}

const PlatformSelection = ({
  currentStep,
  totalSteps,
  onNextButtonClick,
  onBackButtonClick,
}: PlatformSelectionProps) => {
  const { watch, reset, getValues } = useFormContext();

  const commerceType = watch('commerceType');

  return (
    <StepContent>
      <ProgressBar totalSteps={totalSteps} completedSteps={currentStep} stepLabel="Platform" />

      <SectionContent>
        <HeaderContent>
          <Heading variant="Headings/H3" as="h1">
            How would you like to connect to Carro?
          </Heading>

          <Body as="p">
            We sync order data every 15 minutes. Product, inventory, and pricing data is synced
            every hour.{' '}
            <SupportLink article="/integrations/supported-platforms">Learn more</SupportLink>
          </Body>
        </HeaderContent>

        {commerceType === CommerceType.Buyer && <BuyerPlatformFields />}
        {commerceType === CommerceType.Seller && <SellerPlatformFields />}

        <FooterRow>
          <TertiaryButton
            $iconName="chevron-left"
            $removePadding="left"
            onClick={() => {
              onBackButtonClick();

              reset({
                ...getValues(),
                platform: Platform.Shopify,
              });
            }}
          >
            Back
          </TertiaryButton>

          <PrimaryButton $iconName="chevron-right" $iconAfter onClick={onNextButtonClick}>
            Next
          </PrimaryButton>
        </FooterRow>
      </SectionContent>
    </StepContent>
  );
};

export default PlatformSelection;
